// Cliente HTTP que pega a lealify-base (mismo origen). La sesión va por cookie httpOnly.
// RECABLEO F4 (vertical letsmove sobre lealify-base):
//  - DATOS de la vertical viven bajo /api/letsmove/* (rutas routes/letsmove-*.js).
//  - AUTH de operador vive bajo /api/auth/* (login/logout/me/forgot/reset del núcleo).
// El enrutado se decide por el prefijo del path para no tocar cada feature.
import { registrarError } from './telemetry';

export const DATA_BASE = '/api/letsmove';   // datos de la vertical
export const AUTH_BASE = '/api';            // auth del núcleo (/auth/*)

function baseFor(path: string): string {
  // /auth/*, /me/* y /tasks* son del NÚCLEO (/api), no de la vertical (/api/letsmove).
  // /tasks = módulo CROSS compartido (2026-07-01). OJO: usar el límite '/me/' (con slash) —
  // '/me' a secas colisiona con /members, /memberships, /messages.
  // /products = módulo core Vitrina (catálogo multitenant compartido con la agenda dental).
  // /billing y /uploads = módulo core Mi plan (fase C, 18-ago-2026): suscripción, pagos y
  // el token de subida del comprobante viven en el núcleo, no en la vertical.
  // /help = bot de ayuda del núcleo (POST /api/help/ask, 2026-08-31): responde por vertical.
  return (path.startsWith('/auth') || path.startsWith('/me/') || path.startsWith('/tasks') || path.startsWith('/biz/') || path.startsWith('/products') || path.startsWith('/billing') || path.startsWith('/uploads') || path.startsWith('/help')) ? AUTH_BASE : DATA_BASE;
}

export class ApiError extends Error {
  status: number;
  /** Cuerpo completo de la respuesta de error: algunos endpoints mandan datos útiles
   *  junto al mensaje (p. ej. DELETE /classes → { enrolled, can_force }). */
  data: any;
  constructor(status: number, message: string, data?: any) {
    super(message);
    this.status = status;
    this.data = data ?? null;
  }
}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const res = await fetch(`${baseFor(path)}${path}`, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...(options.headers || {}) },
    ...options,
  });

  let body: any = null;
  const text = await res.text();
  if (text) {
    try { body = JSON.parse(text); } catch { body = text; }
  }

  if (!res.ok) {
    const msg = (body && body.error) || res.statusText || `HTTP ${res.status}`;
    // LECTURAS rechazadas (403 = «no tengo permiso para ver esto», 5xx = falló el servidor) quedan
    // registradas: las ESCRITURAS fallidas ya las guarda la auditoría del servidor con su usuario.
    // 401 (sesión vencida) y 404 (p. ej. alumna sin foto) son normales y no se registran.
    const metodo = (options.method || 'GET').toUpperCase();
    if (metodo === 'GET' && res.status !== 401 && res.status !== 404) registrarError(`${res.status} ${path.split('?')[0]}: ${msg}`);
    throw new ApiError(res.status, msg, body);
  }
  avisaSiCambianAdeudos(path, options.method);
  return body as T;
}

/** Evento que escucha DebtFlagsContext para refrescar la moneda de adeudo de los avatares. */
export const EVENTO_ADEUDOS = 'lm:adeudos-cambiaron';

// Toda ESCRITURA de cobros o adeudos avisa aquí, en el único sitio por donde pasan todas
// (Carlos 29-sep-2026: fió vitrina y el avatar no se enteró). Antes solo refrescaba QuickPay:
// crear, cancelar o borrar un adeudo, o borrar un pago, dejaban la moneda vieja hasta recargar.
function avisaSiCambianAdeudos(path: string, method?: string) {
  if (!method || method === 'GET' || !/^\/finances\/(debts|payments)(\/|\?|$)/.test(path)) return;
  try { window.dispatchEvent(new Event(EVENTO_ADEUDOS)); } catch { /* la insignia es informativa */ }
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, data?: unknown) =>
    request<T>(path, { method: 'POST', body: data ? JSON.stringify(data) : undefined }),
  put: <T>(path: string, data?: unknown) =>
    request<T>(path, { method: 'PUT', body: data ? JSON.stringify(data) : undefined }),
  patch: <T>(path: string, data?: unknown) =>
    request<T>(path, { method: 'PATCH', body: data ? JSON.stringify(data) : undefined }),
  del: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
};
